<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import EmojiIcon from 'dashboard/components-next/emoji-icon-picker/EmojiIcon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import MenuItem from './MenuItem.vue';

const props = defineProps({
  menuItems: {
    type: Array,
    default: () => [],
  },
  menuSections: {
    type: Array,
    default: () => [],
  },
  thumbnailSize: {
    type: Number,
    default: 20,
  },
  roundedThumbnail: {
    type: Boolean,
    default: true,
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
  searchPlaceholder: {
    type: String,
    default: '',
  },
  isSearching: {
    type: Boolean,
    default: false,
  },
  labelClass: {
    type: String,
    default: '',
  },
  disableLocalFiltering: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  emptyStateMessage: {
    type: String,
    default: 'DROPDOWN_MENU.EMPTY_STATE',
  },
  compact: {
    type: Boolean,
    default: false,
  },
  // Optional form-picker behavior; action menus keep their existing defaults.
  portal: { type: Boolean, default: false },
  listbox: { type: Boolean, default: false },
  multiple: { type: Boolean, default: false },
  searchValue: { type: String, default: undefined },
  emptyState: { type: String, default: '' },
  scrollClass: { type: String, default: '' },
  showSectionDividers: { type: Boolean, default: true },
  autoFocus: { type: Boolean, default: true },
});

const emit = defineEmits(['action', 'search', 'empty']);

const { t } = useI18n();

const searchInput = ref(null);
const menuRef = ref(null);
const searchQuery = ref(props.searchValue ?? '');
watch(
  () => props.searchValue,
  value => {
    if (value !== undefined) searchQuery.value = value;
  }
);

const hasSections = computed(() => props.menuSections.length > 0);

const flattenedMenuItems = computed(() => {
  if (!hasSections.value) {
    return props.menuItems;
  }

  return props.menuSections.flatMap(section => section.items || []);
});

const filteredMenuItems = computed(() => {
  if (props.disableLocalFiltering) return props.menuItems;
  if (!searchQuery.value) return flattenedMenuItems.value;

  return flattenedMenuItems.value.filter(item =>
    item.label.toLowerCase().includes(searchQuery.value.toLowerCase())
  );
});

const filteredMenuSections = computed(() => {
  if (!hasSections.value) {
    return [];
  }

  if (props.disableLocalFiltering || !searchQuery.value) {
    return props.menuSections;
  }

  const query = searchQuery.value.toLowerCase();

  return props.menuSections
    .map(section => {
      const filteredItems = (section.items || []).filter(item =>
        item.label.toLowerCase().includes(query)
      );

      return {
        ...section,
        items: filteredItems,
      };
    })
    .filter(section => section.items.length > 0);
});

const handleSearchInput = event => {
  emit('search', event.target.value);

  const isEmpty = hasSections.value
    ? filteredMenuSections.value.length === 0
    : filteredMenuItems.value.length === 0;

  if (isEmpty) emit('empty');
};

const handleAction = item => {
  const { action, value, ...rest } = item;
  emit('action', { action, value, ...rest });
};

const handleItemKeydown = (event, item) => {
  if (!props.listbox || !['Enter', ' '].includes(event.key)) return;
  event.preventDefault();
  handleAction(item);
};

const shouldShowEmptyState = computed(() => {
  if (hasSections.value) {
    return filteredMenuSections.value.length === 0;
  }

  return filteredMenuItems.value.length === 0;
});

onMounted(() => {
  if (props.autoFocus && searchInput.value && props.showSearch) {
    searchInput.value.focus();
  }
});

defineExpose({
  focus: () =>
    (searchInput.value || menuRef.value)?.focus({ preventScroll: true }),
});
</script>

<template>
  <div
    ref="menuRef"
    :tabindex="listbox ? -1 : undefined"
    class="bg-n-alpha-3 backdrop-blur-[100px] border-0 outline outline-1 outline-n-container z-50 flex flex-col min-w-[136px] shadow-lg overflow-hidden"
    :class="[
      portal ? 'fixed' : 'absolute',
      compact ? 'rounded-lg pt-1' : 'rounded-xl pt-2',
    ]"
  >
    <div
      v-if="showSearch"
      class="relative shrink-0"
      :class="compact ? 'px-1 mb-1' : 'px-2 mb-2'"
    >
      <slot name="search-icon">
        <span
          class="absolute i-lucide-search size-3.5"
          :class="
            compact
              ? 'top-1.5 ltr:left-3 rtl:right-3'
              : 'top-2 ltr:left-5 rtl:right-5'
          "
        />
      </slot>
      <input
        ref="searchInput"
        v-model="searchQuery"
        type="search"
        :aria-label="searchPlaceholder || t('DROPDOWN_MENU.SEARCH_PLACEHOLDER')"
        :placeholder="
          searchPlaceholder || t('DROPDOWN_MENU.SEARCH_PLACEHOLDER')
        "
        class="reset-base w-full focus:outline-none border-none rounded-md bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-11 placeholder:opacity-100"
        :class="
          compact
            ? 'h-7 py-1 text-xs ltr:pl-8 ltr:pr-1.5 rtl:pl-1.5 rtl:pr-8'
            : 'h-8 py-2 text-sm ltr:pl-10 ltr:pr-2 rtl:pl-2 rtl:pr-10 rounded-lg'
        "
        @input="handleSearchInput"
      />
    </div>
    <div
      :role="listbox ? 'listbox' : undefined"
      :aria-multiselectable="listbox ? multiple : undefined"
      class="flex flex-col overflow-y-auto min-h-0"
      :class="[compact ? 'gap-0.5 px-1 pb-1' : 'gap-2 px-2 pb-2', scrollClass]"
    >
      <slot name="content">
        <template v-if="hasSections">
          <div
            v-for="(section, sectionIndex) in filteredMenuSections"
            :key="section.title || sectionIndex"
            class="flex flex-col gap-1"
          >
            <p
              v-if="section.title"
              class="px-2 py-2 text-xs mb-0 font-medium text-n-slate-11 uppercase tracking-wide sticky top-0 z-10 bg-n-alpha-3 backdrop-blur-sm truncate min-w-0"
            >
              {{ section.title }}
            </p>
            <div
              v-if="section.isLoading"
              class="flex items-center justify-center py-2"
            >
              <Spinner :size="24" />
            </div>
            <div
              v-else-if="!section.items.length && section.emptyState"
              class="text-sm text-n-slate-11 px-2 py-1.5"
            >
              {{ section.emptyState }}
            </div>
            <MenuItem
              v-for="(item, itemIndex) in section.items"
              :key="item.value || itemIndex"
              :compact="compact"
              :selected="item.isSelected"
              :action="item.action"
              :disabled="item.disabled"
              :title="item.title"
              :data-testid="item.testId"
              :role="listbox ? 'option' : undefined"
              :aria-selected="listbox ? Boolean(item.isSelected) : undefined"
              @click="handleAction(item)"
              @keydown="handleItemKeydown($event, item)"
            >
              <slot name="thumbnail" :item="item">
                <Avatar
                  v-if="item.thumbnail"
                  :name="item.thumbnail.name"
                  :src="item.thumbnail.src"
                  :size="thumbnailSize"
                  :rounded-full="roundedThumbnail"
                />
              </slot>
              <slot name="icon" :item="item">
                <Icon
                  v-if="item.icon"
                  :icon="item.icon"
                  class="flex-shrink-0 size-3.5"
                />
              </slot>
              <EmojiIcon
                v-if="item.emoji"
                :value="item.emoji"
                :color="item.iconColor"
                class="flex-shrink-0 size-4"
              />
              <slot name="label" :item="item">
                <span
                  v-if="item.label"
                  class="min-w-0 text-sm font-420 whitespace-normal break-words"
                  :class="labelClass"
                >
                  {{ item.label }}
                </span>
              </slot>
              <slot name="trailing-icon" :item="item" />
            </MenuItem>
            <div
              v-if="
                showSectionDividers &&
                sectionIndex < filteredMenuSections.length - 1
              "
              class="h-px bg-n-alpha-2 mx-2 my-1"
            />
          </div>
        </template>
        <template v-else>
          <div v-if="isLoading" class="flex items-center justify-center py-2">
            <Spinner :size="24" />
          </div>
          <MenuItem
            v-for="(item, index) in filteredMenuItems"
            :key="index"
            :compact="compact"
            :selected="item.isSelected"
            :action="item.action"
            :disabled="item.disabled"
            :title="item.title"
            :data-testid="item.testId"
            :role="listbox ? 'option' : undefined"
            :aria-selected="listbox ? Boolean(item.isSelected) : undefined"
            @click="handleAction(item)"
            @keydown="handleItemKeydown($event, item)"
          >
            <slot name="thumbnail" :item="item">
              <Avatar
                v-if="item.thumbnail"
                :name="item.thumbnail.name"
                :src="item.thumbnail.src"
                :size="thumbnailSize"
                :rounded-full="roundedThumbnail"
              />
            </slot>
            <slot name="icon" :item="item">
              <Icon
                v-if="item.icon"
                :icon="item.icon"
                class="flex-shrink-0 size-3.5"
              />
            </slot>
            <EmojiIcon
              v-if="item.emoji"
              :value="item.emoji"
              :color="item.iconColor"
              class="flex-shrink-0 size-4"
            />
            <slot name="label" :item="item">
              <span
                v-if="item.label"
                class="min-w-0 text-sm font-420 whitespace-normal break-words"
                :class="labelClass"
              >
                {{ item.label }}
              </span>
            </slot>
            <slot name="trailing-icon" :item="item" />
          </MenuItem>
        </template>
        <div
          v-if="shouldShowEmptyState"
          class="text-sm text-n-slate-11 px-2 py-1.5"
        >
          {{
            emptyState ||
            (isSearching
              ? t('DROPDOWN_MENU.SEARCHING')
              : searchQuery
                ? t('DROPDOWN_MENU.EMPTY_STATE')
                : t(emptyStateMessage))
          }}
        </div>
      </slot>
    </div>
    <div v-if="$slots.footer" class="shrink-0">
      <slot name="footer" />
    </div>
  </div>
</template>
